/**
 * waap-prog-mitigation-tab.tsx — Programmable Mitigation (PBR) tab (B3 batch).
 *
 * Two-panel rule engine under one tab:
 *   - WAC rules  (Web Access Control)  — /pbr/web-access-controls
 *   - Rate Limit rules                 — /pbr/rate-limitings
 *
 * Each panel: list → inline "New rule" form → Edit/Delete per row.
 * All mutations via ActAsMutationConfirm (reason required).
 *
 * NOTE: The spec's /pbr/enabled-sites endpoint is private (SSO-only, not on
 * the public Open-API). The "websites" field is accepted on write but we
 * cannot populate a picker from it. We surface an inline note and let
 * operators enter numeric IDs manually (comma-separated).
 *
 * Data sources (anchor-scoped):
 *   GET/POST/PUT/DELETE /waap/domains/:anchorId/pbr/web-access-controls[/:id]
 *   GET/POST/PUT/DELETE /waap/domains/:anchorId/pbr/rate-limitings[/:id]
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import {
  Card, CardHeader, CardTitle, CardBody,
  Button, IconButton, Input, Badge,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { EmptyState } from '@vnetwork/ui/data';
import { Field } from '@vnetwork/ui/forms';
import { TabError } from '@/pages/customers/detail/tab-states';
import { ActAsMutationConfirm } from '@/pages/operations/detail/cdn/act-as-mutation-confirm';
import { actAsClient } from '@/api/act-as-client';
import { actAsWAAPKeys } from '@/api/act-as-waap';
import {
  useActAsWAAPPBRWebAccessControls,
  useActAsWAAPPBRRateLimitings,
  PBR_PATH_OPERATORS,
  PBR_KEY_TYPES,
  PBR_WINDOW_UNITS,
  type PBRWebAccessControlRule,
  type PBRRateLimitingRule,
  type PbrPathOperator,
  type PbrKeyType,
  type PbrWindowUnit,
} from '@/api/act-as-waap-account';
import { NumericInput } from '@/components/numeric-input';

/* ── Shared helpers ──────────────────────────────────────────────────────── */

function parseIdList(text: string): number[] {
  return text
    .split(/[\s,]+/)
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

function formatIdList(ids: number[] | undefined): string {
  return (ids ?? []).join(', ');
}

/* ─────────────────────────────────────────────────────────────────────────
   WAC (Web Access Control) rules panel
   ────────────────────────────────────────────────────────────────────────── */

const EMPTY_WAC: PBRWebAccessControlRule = {
  name: '',
  path_operator: 'prefix',
  path_pattern: '',
  key_type: 'ip',
  websites: [],
};

interface WacPending {
  kind: 'create' | 'update' | 'delete';
  rule: PBRWebAccessControlRule;
}

function WacRulesPanel({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const wacQ = useActAsWAAPPBRWebAccessControls(anchorId);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing]   = useState<PBRWebAccessControlRule | null>(null);
  const [draft, setDraft]       = useState<PBRWebAccessControlRule>(EMPTY_WAC);
  const [websitesDraft, setWebsitesDraft] = useState('');
  const [pending, setPending]   = useState<WacPending | null>(null);

  function openNew() {
    setEditing(null);
    setDraft({ ...EMPTY_WAC });
    setWebsitesDraft('');
    setFormOpen(true);
  }

  function openEdit(rule: PBRWebAccessControlRule) {
    setEditing(rule);
    setDraft({ ...rule });
    setWebsitesDraft(formatIdList(rule.websites));
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function handleSubmit() {
    const payload: PBRWebAccessControlRule = {
      ...draft,
      websites: parseIdList(websitesDraft),
    };
    setPending({ kind: editing ? 'update' : 'create', rule: payload });
  }

  function buildMutationFn(p: WacPending) {
    const base = `/waap/domains/${anchorId}/pbr/web-access-controls`;
    if (p.kind === 'create') return () => actAsClient.post(base, p.rule);
    if (p.kind === 'update') return () => actAsClient.put(`${base}/${p.rule.id}`, p.rule);
    return () => actAsClient.delete(`${base}/${p.rule.id}`);
  }

  function patch(key: keyof PBRWebAccessControlRule, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const isValid = draft.name.trim() !== '' && draft.path_pattern.trim() !== '';

  if (wacQ.isLoading) return <Skeleton style={{ width: '100%', height: 100 }} />;
  if (wacQ.isError) {
    return (
      <TabError
        title={t('waap_domain.pbr_wac_load_error', 'Failed to load WAC rules')}
        onRetry={() => wacQ.refetch()}
      />
    );
  }

  const rules = wacQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          {t('waap_domain.pbr_wac_title', 'Web Access Control Rules')}
          <Badge style={{ marginLeft: 'var(--vn-space-2)' }}>{rules.length}</Badge>
        </CardTitle>
        <Button size="sm" variant="secondary" onClick={openNew}>
          <Plus size={13} />
          {t('waap_domain.pbr_add_rule', 'Add rule')}
        </Button>
      </div>

      {/* Rule list */}
      {rules.length === 0 && !formOpen ? (
        <EmptyState
          title={t('waap_domain.pbr_wac_empty', 'No WAC rules')}
          description={t('waap_domain.pbr_wac_empty_desc', 'Add a Web Access Control rule to control traffic patterns.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule) => (
            <Card key={rule.id}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rule.name}
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                    {rule.path_operator} {rule.path_pattern} · key: {rule.key_type}
                    {(rule.websites?.length ?? 0) > 0 && ` · sites: ${rule.websites!.join(', ')}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                  <IconButton size="sm" variant="ghost" aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(rule)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={t('common.delete', 'Delete')}
                    onClick={() => setPending({ kind: 'delete', rule })}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* Inline create/edit form */}
      {formOpen && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              {editing
                ? t('waap_domain.pbr_wac_edit', 'Edit WAC rule')
                : t('waap_domain.pbr_wac_new', 'New WAC rule')}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label={t('waap_domain.pbr_field_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={draft.name} onChange={(e) => patch('name', e.target.value)} placeholder="Rule name" />
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_path_op', 'Path operator')}>
                {(p) => (
                  <Select value={draft.path_operator} onValueChange={(v) => patch('path_operator', v as PbrPathOperator)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBR_PATH_OPERATORS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_path', 'Path pattern')}>
                {(p) => (
                  <Input {...p} value={draft.path_pattern} onChange={(e) => patch('path_pattern', e.target.value)} placeholder="/api/*" />
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_key_type', 'Key type')}>
                {(p) => (
                  <Select value={draft.key_type} onValueChange={(v) => patch('key_type', v as PbrKeyType)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBR_KEY_TYPES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_prob', 'Triggering probability (0-100)')}>
                {(p) => (
                  <NumericInput
                    {...p}
                    min={0}
                    max={100}
                    value={draft.triggering_probability != null ? String(draft.triggering_probability) : ''}
                    onChange={(e) => patch('triggering_probability', parseInt(e.target.value, 10) || 0)}
                    placeholder="100"
                  />
                )}
              </Field>
            </div>
            <Field
              label={t('waap_domain.pbr_field_desc', 'Description')}
            >
              {(p) => (
                <Input {...p} value={draft.description ?? ''} onChange={(e) => patch('description', e.target.value)} placeholder="Optional description" />
              )}
            </Field>
            <Field
              label={t('waap_domain.pbr_field_websites', 'Website IDs (comma-separated)')}
              description={t('waap_domain.pbr_websites_note', 'Vendor site IDs. The enabled-sites picker is unavailable (private route) — enter IDs manually.')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={websitesDraft}
                  onChange={(e) => setWebsitesDraft(e.target.value)}
                  placeholder="1, 2, 3"
                />
              )}
            </Field>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={closeForm}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="sm" variant="primary" disabled={!isValid} onClick={handleSubmit}>
                {editing ? t('common.save', 'Save') : t('common.create', 'Create')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Confirm dialog */}
      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={
            pending.kind === 'delete'
              ? t('waap_domain.pbr_wac_delete_title', 'Delete WAC rule')
              : pending.kind === 'create'
              ? t('waap_domain.pbr_wac_create_title', 'Create WAC rule')
              : t('waap_domain.pbr_wac_update_title', 'Update WAC rule')
          }
          summary={
            pending.kind === 'delete'
              ? t('waap_domain.pbr_wac_delete_summary', 'Permanently delete WAC rule "{{name}}".', { name: pending.rule.name })
              : t('waap_domain.pbr_wac_save_summary', 'Save WAC rule "{{name}}" for path {{path}}.', {
                  name: pending.rule.name,
                  path: pending.rule.path_pattern,
                })
          }
          destructive={pending.kind === 'delete'}
          queryKeysToInvalidate={[actAsWAAPKeys.aspect(anchorId, 'pbr/web-access-controls')]}
          mutationFn={buildMutationFn(pending)}
          onSuccess={() => { setPending(null); closeForm(); wacQ.refetch(); }}
        />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   Rate Limiting rules panel
   ────────────────────────────────────────────────────────────────────────── */

const EMPTY_RL: PBRRateLimitingRule = {
  name: '',
  path_operator: 'prefix',
  path_pattern: '',
  key_type: 'ip',
  window_unit: 'minute',
  max_requests: 100,
  websites: [],
};

interface RlPending {
  kind: 'create' | 'update' | 'delete';
  rule: PBRRateLimitingRule;
}

function RateLimitRulesPanel({ anchorId }: { anchorId: string }) {
  const { t } = useTranslation();
  const rlQ = useActAsWAAPPBRRateLimitings(anchorId);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing]   = useState<PBRRateLimitingRule | null>(null);
  const [draft, setDraft]       = useState<PBRRateLimitingRule>(EMPTY_RL);
  const [websitesDraft, setWebsitesDraft] = useState('');
  const [pending, setPending]   = useState<RlPending | null>(null);

  function openNew() {
    setEditing(null);
    setDraft({ ...EMPTY_RL });
    setWebsitesDraft('');
    setFormOpen(true);
  }

  function openEdit(rule: PBRRateLimitingRule) {
    setEditing(rule);
    setDraft({ ...rule });
    setWebsitesDraft(formatIdList(rule.websites));
    setFormOpen(true);
  }

  function closeForm() {
    setFormOpen(false);
    setEditing(null);
  }

  function handleSubmit() {
    const payload: PBRRateLimitingRule = {
      ...draft,
      websites: parseIdList(websitesDraft),
    };
    setPending({ kind: editing ? 'update' : 'create', rule: payload });
  }

  function buildMutationFn(p: RlPending) {
    const base = `/waap/domains/${anchorId}/pbr/rate-limitings`;
    if (p.kind === 'create') return () => actAsClient.post(base, p.rule);
    if (p.kind === 'update') return () => actAsClient.put(`${base}/${p.rule.id}`, p.rule);
    return () => actAsClient.delete(`${base}/${p.rule.id}`);
  }

  function patch(key: keyof PBRRateLimitingRule, value: unknown) {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }

  const isValid =
    draft.name.trim() !== '' &&
    draft.path_pattern.trim() !== '' &&
    draft.max_requests > 0;

  if (rlQ.isLoading) return <Skeleton style={{ width: '100%', height: 100 }} />;
  if (rlQ.isError) {
    return (
      <TabError
        title={t('waap_domain.pbr_rl_load_error', 'Failed to load rate limit rules')}
        onRetry={() => rlQ.refetch()}
      />
    );
  }

  const rules = rlQ.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <CardTitle style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
          {t('waap_domain.pbr_rl_title', 'Rate Limiting Rules')}
          <Badge style={{ marginLeft: 'var(--vn-space-2)' }}>{rules.length}</Badge>
        </CardTitle>
        <Button size="sm" variant="secondary" onClick={openNew}>
          <Plus size={13} />
          {t('waap_domain.pbr_add_rule', 'Add rule')}
        </Button>
      </div>

      {rules.length === 0 && !formOpen ? (
        <EmptyState
          title={t('waap_domain.pbr_rl_empty', 'No rate limit rules')}
          description={t('waap_domain.pbr_rl_empty_desc', 'Add a rate limiting rule to throttle abusive traffic patterns.')}
        />
      ) : (
        <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
          {rules.map((rule) => (
            <Card key={rule.id}>
              <CardBody style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-3)', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 'var(--vn-font-size-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {rule.name}
                  </div>
                  <div style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)', marginTop: 2 }}>
                    {rule.path_operator} {rule.path_pattern} · {rule.max_requests} req/{rule.window_unit} · key: {rule.key_type}
                    {(rule.websites?.length ?? 0) > 0 && ` · sites: ${rule.websites!.join(', ')}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
                  <IconButton size="sm" variant="ghost" aria-label={t('common.edit', 'Edit')} onClick={() => openEdit(rule)}>
                    <Pencil size={13} />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    aria-label={t('common.delete', 'Delete')}
                    onClick={() => setPending({ kind: 'delete', rule })}
                  >
                    <Trash2 size={13} />
                  </IconButton>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {formOpen && (
        <Card>
          <CardHeader>
            <CardTitle style={{ fontSize: 'var(--vn-font-size-sm)' }}>
              {editing
                ? t('waap_domain.pbr_rl_edit', 'Edit rate limit rule')
                : t('waap_domain.pbr_rl_new', 'New rate limit rule')}
            </CardTitle>
          </CardHeader>
          <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 'var(--vn-space-3)' }}>
              <Field label={t('waap_domain.pbr_field_name', 'Name')}>
                {(p) => (
                  <Input {...p} value={draft.name} onChange={(e) => patch('name', e.target.value)} placeholder="Rule name" />
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_path_op', 'Path operator')}>
                {(p) => (
                  <Select value={draft.path_operator} onValueChange={(v) => patch('path_operator', v as PbrPathOperator)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBR_PATH_OPERATORS.map((op) => <SelectItem key={op} value={op}>{op}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_path', 'Path pattern')}>
                {(p) => (
                  <Input {...p} value={draft.path_pattern} onChange={(e) => patch('path_pattern', e.target.value)} placeholder="/api/*" />
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_key_type', 'Key type')}>
                {(p) => (
                  <Select value={draft.key_type} onValueChange={(v) => patch('key_type', v as PbrKeyType)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBR_KEY_TYPES.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_max_req', 'Max requests')}>
                {(p) => (
                  <NumericInput
                    {...p}
                    min={1}
                    value={String(draft.max_requests)}
                    onChange={(e) => patch('max_requests', parseInt(e.target.value, 10) || 1)}
                  />
                )}
              </Field>
              <Field label={t('waap_domain.pbr_field_window', 'Window unit')}>
                {(p) => (
                  <Select value={draft.window_unit} onValueChange={(v) => patch('window_unit', v as PbrWindowUnit)}>
                    <SelectTrigger {...p}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PBR_WINDOW_UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>
            <Field label={t('waap_domain.pbr_field_desc', 'Description')}>
              {(p) => (
                <Input {...p} value={draft.description ?? ''} onChange={(e) => patch('description', e.target.value)} placeholder="Optional description" />
              )}
            </Field>
            <Field
              label={t('waap_domain.pbr_field_websites', 'Website IDs (comma-separated)')}
              description={t('waap_domain.pbr_websites_note', 'Vendor site IDs. The enabled-sites picker is unavailable (private route) — enter IDs manually.')}
            >
              {(p) => (
                <Input
                  {...p}
                  value={websitesDraft}
                  onChange={(e) => setWebsitesDraft(e.target.value)}
                  placeholder="1, 2, 3"
                />
              )}
            </Field>
            <div style={{ display: 'flex', gap: 'var(--vn-space-2)', justifyContent: 'flex-end' }}>
              <Button size="sm" variant="secondary" onClick={closeForm}>
                {t('common.cancel', 'Cancel')}
              </Button>
              <Button size="sm" variant="primary" disabled={!isValid} onClick={handleSubmit}>
                {editing ? t('common.save', 'Save') : t('common.create', 'Create')}
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {pending && (
        <ActAsMutationConfirm
          open
          onOpenChange={(open) => { if (!open) setPending(null); }}
          title={
            pending.kind === 'delete'
              ? t('waap_domain.pbr_rl_delete_title', 'Delete rate limit rule')
              : pending.kind === 'create'
              ? t('waap_domain.pbr_rl_create_title', 'Create rate limit rule')
              : t('waap_domain.pbr_rl_update_title', 'Update rate limit rule')
          }
          summary={
            pending.kind === 'delete'
              ? t('waap_domain.pbr_rl_delete_summary', 'Permanently delete rate limit rule "{{name}}".', { name: pending.rule.name })
              : t('waap_domain.pbr_rl_save_summary', 'Save rate limit rule "{{name}}" — {{max}} req/{{unit}} on {{path}}.', {
                  name: pending.rule.name,
                  max: pending.rule.max_requests,
                  unit: pending.rule.window_unit,
                  path: pending.rule.path_pattern,
                })
          }
          destructive={pending.kind === 'delete'}
          queryKeysToInvalidate={[actAsWAAPKeys.aspect(anchorId, 'pbr/rate-limitings')]}
          mutationFn={buildMutationFn(pending)}
          onSuccess={() => { setPending(null); closeForm(); rlQ.refetch(); }}
        />
      )}
    </div>
  );
}

/* ── Root component ──────────────────────────────────────────────────────── */

interface WaapProgMitigationTabProps {
  anchorId: string;
}

/**
 * Programmable Mitigation tab — WAC rules + Rate Limit rules in two sub-tabs.
 *
 * Note: /pbr/enabled-sites is a private SSO-only route not on the public
 * Open-API. The website-ID picker is replaced by a manual text input;
 * an inline Alert explains the limitation.
 */
export function WaapProgMitigationTab({ anchorId }: WaapProgMitigationTabProps) {
  const { t } = useTranslation();
  const [subTab, setSubTab] = useState<'wac' | 'rate-limit'>('wac');

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Alert tone="info" style={{ fontSize: 'var(--vn-font-size-xs)' }}>
        {t(
          'waap_domain.pbr_enabled_sites_note',
          'Website picker unavailable: /pbr/enabled-sites is a private SSO-only endpoint not exposed on the public Open-API. Enter vendor website IDs manually when creating or editing rules.',
        )}
      </Alert>

      <Tabs value={subTab} onValueChange={(v) => setSubTab(v as typeof subTab)}>
        <TabsList>
          <TabsTrigger value="wac">
            {t('waap_domain.pbr_tab_wac', 'Web Access Control')}
          </TabsTrigger>
          <TabsTrigger value="rate-limit">
            {t('waap_domain.pbr_tab_rl', 'Rate Limiting')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="wac">
          {subTab === 'wac' && <WacRulesPanel anchorId={anchorId} />}
        </TabsContent>

        <TabsContent value="rate-limit">
          {subTab === 'rate-limit' && <RateLimitRulesPanel anchorId={anchorId} />}
        </TabsContent>
      </Tabs>
    </div>
  );
}
